import React, { useState, useEffect } from 'react';
import { config } from '../../config/environment';
import './ProviderApproval.css';

interface PendingProvider {
  userUuid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: number;
  dob: string;
  roles: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

type ProviderStatus = 'ACTIVE' | 'UNDER_REVIEW' | 'INACTIVE' | 'SUSPENDED';

const ProviderApproval: React.FC = () => {
  const [pendingProviders, setPendingProviders] = useState<PendingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingProvider, setUpdatingProvider] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<PendingProvider | null>(null);
  const [providerDetails, setProviderDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchPendingProviders();
  }, []);

  const fetchPendingProviders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await fetch(
        `${config.authServerUrl}/api/admin/users/pending-approval`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data: PendingProvider[] = await response.json();
        setPendingProviders(data);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to fetch pending providers:', err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const updateProviderStatus = async (userUuid: string, status: ProviderStatus) => {
    try {
      setUpdatingProvider(userUuid);
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await fetch(
        `${config.authServerUrl}/api/admin/users/${userUuid}/status?status=${status}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const updatedProvider: PendingProvider = await response.json();
        
        // Remove from pending list if status is no longer UNDER_REVIEW
        if (updatedProvider.status !== 'UNDER_REVIEW') {
          setPendingProviders(prev => prev.filter(p => p.userUuid !== userUuid));
        } else {
          // Update the provider in the list
          setPendingProviders(prev => 
            prev.map(p => p.userUuid === userUuid ? updatedProvider : p)
          );
        }
        
        console.log(`Provider ${userUuid} status updated to ${status}`);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error(`Failed to update provider status:`, err);
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setUpdatingProvider(null);
    }
  };

  const handleApprove = (userUuid: string) => {
    updateProviderStatus(userUuid, 'ACTIVE');
  };

  const handleReject = (userUuid: string) => {
    updateProviderStatus(userUuid, 'INACTIVE');
  };

  const handleHold = (userUuid: string) => {
    updateProviderStatus(userUuid, 'SUSPENDED');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const fetchProviderDetails = async (provider: PendingProvider) => {
    try {
      setDetailsLoading(true);
      setSelectedProvider(provider);
      setShowDetailsModal(true);
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }

      const response = await fetch(
        `${config.authServerUrl}/api/provider/${provider.userUuid}/profile`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const details = await response.json();
        setProviderDetails(details);
      } else if (response.status === 404) {
        // Provider hasn't completed profile yet
        setProviderDetails(null);
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      console.error('Failed to fetch provider details:', err);
      setProviderDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedProvider(null);
    setProviderDetails(null);
  };

  if (loading) {
    return (
      <div className="provider-approval">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading pending provider requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="provider-approval">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchPendingProviders} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="provider-approval">
      <div className="approval-header">
        <h2>Provider Approval Requests</h2>
        <p className="approval-subtitle">
          Review and approve new provider registrations
        </p>
        <button onClick={fetchPendingProviders} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>

      {pendingProviders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Pending Requests</h3>
          <p>All provider requests have been processed.</p>
        </div>
      ) : (
        <div className="providers-grid">
          {pendingProviders.map((provider) => (
            <div key={provider.userUuid} className="provider-card">
              <div className="provider-info">
                <div className="provider-header">
                  <h3>{provider.firstName} {provider.lastName}</h3>
                  <span className={`status-badge ${provider.status.toLowerCase()}`}>
                    {provider.status.replace('_', ' ')}
                  </span>
                </div>
                
                <div className="provider-details">
                  <div className="detail-row">
                    <span className="detail-label">📧 Email:</span>
                    <span className="detail-value">{provider.email}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📱 Phone:</span>
                    <span className="detail-value">{provider.phone}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">🎂 DOB:</span>
                    <span className="detail-value">{new Date(provider.dob).toLocaleDateString()}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📅 Applied:</span>
                    <span className="detail-value">{formatDate(provider.createdAt)}</span>
                  </div>
                </div>
              </div>

              <div className="provider-actions">
                <button
                  className="action-btn view-details-btn"
                  onClick={() => fetchProviderDetails(provider)}
                >
                  👁️ View Details
                </button>
                <button
                  className="action-btn approve-btn"
                  onClick={() => handleApprove(provider.userUuid)}
                  disabled={updatingProvider === provider.userUuid}
                >
                  {updatingProvider === provider.userUuid ? '⏳' : '✅'} Approve
                </button>
                <button
                  className="action-btn reject-btn"
                  onClick={() => handleReject(provider.userUuid)}
                  disabled={updatingProvider === provider.userUuid}
                >
                  {updatingProvider === provider.userUuid ? '⏳' : '❌'} Reject
                </button>
                <button
                  className="action-btn hold-btn"
                  onClick={() => handleHold(provider.userUuid)}
                  disabled={updatingProvider === provider.userUuid}
                >
                  {updatingProvider === provider.userUuid ? '⏳' : '⏸️'} Hold
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Provider Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay" onClick={closeDetailsModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Provider Details</h2>
              <button className="modal-close" onClick={closeDetailsModal}>
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              {detailsLoading ? (
                <div className="details-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading provider details...</p>
                </div>
              ) : selectedProvider ? (
                <div className="provider-full-details">
                  {/* Basic Info Section */}
                  <div className="details-section">
                    <h3>📋 Basic Information</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">Name:</span>
                        <span className="detail-value">{selectedProvider.firstName} {selectedProvider.lastName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Email:</span>
                        <span className="detail-value">{selectedProvider.email}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Phone:</span>
                        <span className="detail-value">{selectedProvider.phone}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Date of Birth:</span>
                        <span className="detail-value">{new Date(selectedProvider.dob).toLocaleDateString()}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Status:</span>
                        <span className={`status-badge ${selectedProvider.status.toLowerCase()}`}>
                          {selectedProvider.status.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Applied On:</span>
                        <span className="detail-value">{formatDate(selectedProvider.createdAt)}</span>
                      </div>
                    </div>
                  </div>

                  {providerDetails ? (
                    <>
                      {/* Professional Info Section */}
                      {providerDetails.professionalInfo && (
                        <div className="details-section">
                          <h3>💼 Professional Information</h3>
                          <div className="details-grid">
                            <div className="detail-item">
                              <span className="detail-label">Experience:</span>
                              <span className="detail-value">{providerDetails.professionalInfo.experienceYears || 'Not specified'} years</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">GST Number:</span>
                              <span className="detail-value">{providerDetails.professionalInfo.gstNo || 'Not provided'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Availability:</span>
                              <span className="detail-value">{providerDetails.professionalInfo.availabilityType || 'Not specified'}</span>
                            </div>
                            {providerDetails.professionalInfo.bio && (
                              <div className="detail-item full-width">
                                <span className="detail-label">Bio:</span>
                                <span className="detail-value">{providerDetails.professionalInfo.bio}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Address Section */}
                      {providerDetails.address && (
                        <div className="details-section">
                          <h3>🏠 Address Information</h3>
                          <div className="details-grid">
                            <div className="detail-item">
                              <span className="detail-label">Address Line 1:</span>
                              <span className="detail-value">{providerDetails.address.line1}</span>
                            </div>
                            {providerDetails.address.line2 && (
                              <div className="detail-item">
                                <span className="detail-label">Address Line 2:</span>
                                <span className="detail-value">{providerDetails.address.line2}</span>
                              </div>
                            )}
                            <div className="detail-item">
                              <span className="detail-label">City:</span>
                              <span className="detail-value">{providerDetails.address.cityName || providerDetails.address.city}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">State:</span>
                              <span className="detail-value">{providerDetails.address.stateName || providerDetails.address.state}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Postal Code:</span>
                              <span className="detail-value">{providerDetails.address.postalCode}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Banking Information Section */}
                      {providerDetails.bankingDocuments && (
                        <div className="details-section">
                          <h3>🏦 Banking Information</h3>
                          <div className="details-grid">
                            <div className="detail-item">
                              <span className="detail-label">Account Holder:</span>
                              <span className="detail-value">{providerDetails.bankingDocuments.accountHolderName || 'Not provided'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Account Number:</span>
                              <span className="detail-value">{providerDetails.bankingDocuments.accountNumber || 'Not provided'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">IFSC Code:</span>
                              <span className="detail-value">{providerDetails.bankingDocuments.ifscCode || 'Not provided'}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Bank Name:</span>
                              <span className="detail-value">{providerDetails.bankingDocuments.bankName || 'Not provided'}</span>
                            </div>
                          </div>

                          {/* Documents Section */}
                          <div className="documents-section">
                            <h4>📄 Uploaded Documents</h4>
                            <div className="documents-grid">
                              <div className="document-item">
                                <span className="document-label">Aadhar Card:</span>
                                <span className="document-status">
                                  {providerDetails.bankingDocuments.aadharCard?.exists ? 
                                    <a href={providerDetails.bankingDocuments.aadharCard.downloadUrl} target="_blank" rel="noopener noreferrer" className="document-link">📎 View Document</a> : 
                                    <span className="not-uploaded">❌ Not uploaded</span>
                                  }
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-label">PAN Card:</span>
                                <span className="document-status">
                                  {providerDetails.bankingDocuments.panCard?.exists ? 
                                    <a href={providerDetails.bankingDocuments.panCard.downloadUrl} target="_blank" rel="noopener noreferrer" className="document-link">📎 View Document</a> : 
                                    <span className="not-uploaded">❌ Not uploaded</span>
                                  }
                                </span>
                              </div>
                              <div className="document-item">
                                <span className="document-label">Business License:</span>
                                <span className="document-status">
                                  {providerDetails.bankingDocuments.miscDoc?.exists ? 
                                    <a href={providerDetails.bankingDocuments.miscDoc.downloadUrl} target="_blank" rel="noopener noreferrer" className="document-link">📎 View Document</a> : 
                                    <span className="not-uploaded">❌ Not uploaded</span>
                                  }
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-details">
                      <div className="no-details-icon">📋</div>
                      <h3>Profile Incomplete</h3>
                      <p>This provider hasn't completed their profile yet. Only basic registration information is available.</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderApproval;